import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UploadSimple, Trash, FileDoc, CheckCircle, WarningCircle, Lock } from "@phosphor-icons/react";

export default function RsawTemplates() {
  const { isSuperadmin } = useAuth();
  const [standards, setStandards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sid, setSid] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);
  const [assignSid, setAssignSid] = useState({}); // template id -> standard id

  const load = useCallback(() => api.get("/rsaw-templates").then((r) => setTemplates(r.data)), []);
  useEffect(() => { api.get("/standards").then((r) => setStandards(r.data)); load(); }, [load]);

  const bulkUpload = async () => {
    if (!bulkFiles.length) return toast.error("Choose one or more RSAW files (.docx, .xlsx, .pdf)");
    setBusy("bulk");
    try {
      const fd = new FormData();
      bulkFiles.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/rsaw-templates/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(data);
      const s = data.summary;
      toast.success(`Bulk import: ${s.created} matched, ${s.queued} need a standard, ${s.skipped} skipped`);
      setBulkFiles([]);
      const el = document.getElementById("rsaw-bulk-file"); if (el) el.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const assignStandard = async (t) => {
    const chosen = assignSid[t.id];
    if (!chosen) return toast.error("Pick a standard for this file");
    setBusy(`assign-${t.id}`);
    try {
      await api.post(`/rsaw-templates/${t.id}/set-standard`, { standard_id: chosen });
      toast.success(`Assigned ${chosen} to ${t.filename}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const upload = async () => {
    if (!sid) return toast.error("Select a standard");
    if (!file) return toast.error("Choose a .docx or .xlsx RSAW file");
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("standard_id", sid);
      fd.append("file", file);
      const { data } = await api.post("/rsaw-templates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const det = data.structure?.requirements_detected?.length || 0;
      toast.success(`Imported ${data.filename} — detected ${det} requirement section(s)`);
      setFile(null); setSid("");
      document.getElementById("rsaw-tpl-file").value = "";
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const fill = async (t) => {
    setBusy(`fill-${t.id}`);
    try {
      const res = await api.get(`/rsaw-templates/${t.id}/fill`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `RSAW_${t.standard_id}_populated.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Populated RSAW downloaded");
    } catch (e) { toast.error("Fill failed"); }
    finally { setBusy(""); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await api.delete(`/rsaw-templates/${id}`); load(); toast.success("Template deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="border border-border bg-secondary/30 p-4 flex items-start gap-2.5" data-testid="rsaw-tpl-global-note">
        <Lock size={16} className="mt-0.5 text-muted-foreground shrink-0" />
        <p className="text-xs text-muted-foreground">
          RSAW templates are a <span className="font-semibold text-foreground">shared global library</span> used by every entity.
          {isSuperadmin
            ? " As a Claro super-admin you can add, assign, and remove templates here — changes apply to all entities."
            : " Adding, editing, or deleting templates is restricted to Claro super-admins so one entity can't change the library for everyone. You can still populate and download any template below."}
        </p>
      </div>

      {isSuperadmin && (<>
      <div className="border border-border bg-secondary/30 p-5" data-testid="rsaw-template-upload">
        <div className="font-display font-bold">Import an official NERC RSAW</div>
        <p className="text-xs text-muted-foreground mb-3">Upload the official RSAW (.docx or .xlsx) for a standard. We parse its requirement sections and evidence requests so the worksheet can be auto-populated from your controls, evidence, narratives and SME assignments.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Standard</label>
            <Select value={sid} onValueChange={setSid}>
              <SelectTrigger data-testid="rsaw-tpl-standard" className="rounded-none w-64 mt-1"><SelectValue placeholder="Select standard" /></SelectTrigger>
              <SelectContent>{standards.map((s) => <SelectItem key={s.standard_id} value={s.standard_id}>{s.standard_id}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground block">RSAW file</label>
            <input id="rsaw-tpl-file" data-testid="rsaw-tpl-file" type="file" accept=".docx,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 text-sm file:mr-3 file:border file:border-border file:bg-card file:px-3 file:py-1.5 file:text-sm file:font-semibold" />
          </div>
          <Button data-testid="rsaw-tpl-upload-btn" onClick={upload} disabled={busy === "upload"} className="rounded-none bg-primary hover:bg-primary/90">
            <UploadSimple size={16} className="mr-1" />{busy === "upload" ? "Importing…" : "Import template"}
          </Button>
        </div>
      </div>

      <div className="border border-border bg-secondary/30 p-5" data-testid="rsaw-bulk-upload">
        <div className="font-display font-bold">Bulk import RSAWs</div>
        <p className="text-xs text-muted-foreground mb-3">
          Download the official RSAWs from <a href="https://www.nerc.com/programs/compliance/reliability-standard-audit-worksheets-rsaws" target="_blank" rel="noreferrer" className="underline">NERC's RSAWs page</a> (NERC posts them as individual files — no bulk feed), then drop them all here at once. We auto-detect each file's standard from its name/content. PDFs are stored for reference (Word/.xlsx can be auto-populated). Files we can't match are queued below for a quick standard pick.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground block">RSAW files (multiple)</label>
            <input id="rsaw-bulk-file" data-testid="rsaw-bulk-file" type="file" multiple accept=".docx,.xlsx,.pdf"
              onChange={(e) => setBulkFiles(Array.from(e.target.files || []))}
              className="mt-1 text-sm file:mr-3 file:border file:border-border file:bg-card file:px-3 file:py-1.5 file:text-sm file:font-semibold" />
          </div>
          <Button data-testid="rsaw-bulk-upload-btn" onClick={bulkUpload} disabled={busy === "bulk"} className="rounded-none bg-primary hover:bg-primary/90">
            <UploadSimple size={16} className="mr-1" />{busy === "bulk" ? `Importing ${bulkFiles.length}…` : `Bulk import${bulkFiles.length ? ` (${bulkFiles.length})` : ""}`}
          </Button>
        </div>
        {bulkResult && (
          <div data-testid="rsaw-bulk-result" className="mt-3 text-xs space-y-1">
            <div className="flex items-center gap-1.5 text-emerald-700"><CheckCircle size={14} weight="fill" />{bulkResult.summary.created} matched & imported</div>
            {bulkResult.queued.length > 0 && <div className="flex items-center gap-1.5 text-amber-700"><WarningCircle size={14} weight="fill" />{bulkResult.summary.queued} need a standard (pick below)</div>}
            {bulkResult.skipped.length > 0 && (
              <div className="text-muted-foreground">{bulkResult.summary.skipped} skipped: {bulkResult.skipped.map((s) => `${s.filename} (${s.reason})`).join(", ")}</div>
            )}
          </div>
        )}
      </div>
      </>)}

      <div className="border border-border bg-card" data-testid="rsaw-template-list">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
            <th className="px-4 py-3">Standard</th><th className="px-4 py-3">File</th><th className="px-4 py-3">Detected</th><th className="px-4 py-3">Uploaded</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {templates.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No templates yet. Import an official RSAW above.</td></tr>}
            {templates.map((t) => {
              const det = t.structure?.requirements_detected || [];
              const matched = det.filter((d) => d.matched).length;
              const needsStd = t.needs_standard || !t.standard_id;
              const refOnly = t.reference_only;
              return (
                <tr key={t.id} data-testid={`rsaw-tpl-row-${t.id}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-mono font-bold">
                    {needsStd ? (
                      isSuperadmin ? (
                      <div className="flex items-center gap-1.5">
                        <Select value={assignSid[t.id] || ""} onValueChange={(v) => setAssignSid((m) => ({ ...m, [t.id]: v }))}>
                          <SelectTrigger data-testid={`rsaw-assign-std-${t.id}`} className="rounded-none w-44 h-8 text-xs"><SelectValue placeholder="Pick standard" /></SelectTrigger>
                          <SelectContent>{standards.map((s) => <SelectItem key={s.standard_id} value={s.standard_id}>{s.standard_id}</SelectItem>)}</SelectContent>
                        </Select>
                        <button data-testid={`rsaw-assign-btn-${t.id}`} onClick={() => assignStandard(t)} disabled={busy === `assign-${t.id}`}
                          className="border border-black px-2 py-1 text-xs font-semibold hover:bg-primary hover:text-white disabled:opacity-50">
                          {busy === `assign-${t.id}` ? "…" : "Assign"}
                        </button>
                      </div>
                      ) : <span className="text-xs font-sans font-normal text-muted-foreground">Awaiting standard</span>
                    ) : t.standard_id}
                  </td>
                  <td className="px-4 py-3"><span className="flex items-center gap-1.5"><FileDoc size={15} />{t.filename}{refOnly && <span className="ml-1 text-[10px] uppercase border border-border px-1 py-0.5 text-muted-foreground">PDF · reference</span>}</span></td>
                  <td className="px-4 py-3">
                    {needsStd ? (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-700"><WarningCircle size={14} weight="fill" />Awaiting standard</span>
                    ) : refOnly ? (
                      <span className="text-xs text-muted-foreground">Reference only (not parsed)</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs">
                        {matched > 0 ? <CheckCircle size={14} className="text-emerald-600" weight="fill" /> : <WarningCircle size={14} className="text-amber-600" weight="fill" />}
                        {det.length} section(s), {matched} matched · {t.structure?.response_anchor_count || 0} response anchors
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{t.created_at?.slice(0, 10)} · {t.uploaded_by}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {!needsStd && !refOnly && (
                      <button data-testid={`rsaw-tpl-fill-${t.id}`} onClick={() => fill(t)} disabled={busy === `fill-${t.id}`}
                        className="border border-black px-2 py-1 text-xs font-semibold hover:bg-primary hover:text-white disabled:opacity-50 mr-2">
                        {busy === `fill-${t.id}` ? "Populating…" : "Populate & download"}
                      </button>
                    )}
                    {isSuperadmin && (
                      <button data-testid={`rsaw-tpl-delete-${t.id}`} onClick={() => remove(t.id)} className="text-muted-foreground hover:text-red-600 align-middle"><Trash size={15} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
